import { cls } from "cls";
import React, { useRef, useState, useCallback } from "react";
import { ModalTrigger } from "@clarkinc/zest-design-system/ui";
import { imageHost } from "@wss/image";
import { Image } from "../../model";
interface ImageProps {
  src: string;
  medium: Image;
  alt: string;
  openZoom?(e: React.MouseEvent, image: Image): void;
  overlayOpen: boolean;
  isMobileScreenSize?: boolean;
  imageNote?: string;
  isIE?: boolean;
  index?: number;
  placeholderImageUrl: string;
}
const PrimaryImage = ({
  src,
  alt,
  medium,
  openZoom,
  overlayOpen,
  isMobileScreenSize = false,
  imageNote = "",
  isIE = false,
  index,
  placeholderImageUrl
}: ImageProps) => {
  const [backgroundPosition, setBackgroundPosition] = useState("0% 0%");
  const [backgroundActive, setBackgroundActive] = useState(false);
  const imageContainerRef = useRef<HTMLElement | null>(null);
  const getImageContainerBounds = useCallback(() => {
    if (!imageContainerRef.current) return null;
    return imageContainerRef.current.getBoundingClientRect();
  }, [imageContainerRef]);
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const {
      clientX,
      clientY
    } = e;
    const containerBounds = getImageContainerBounds();
    if (!imageContainerRef.current || !containerBounds) return;
    const {
      width,
      height,
      left,
      top
    } = containerBounds;
    const x = (clientX - left) / width * 100;
    const y = (clientY - top) / height * 100;
    setBackgroundPosition(`${x}% ${y}%`);
  };
  const zoomEnabled = !!(medium.extraLarge && medium.extraExtraLarge);
  let cursorClasses = "";
  let bgClasses = "";
  let bgImage = `url(${imageHost}${medium.extraExtraLarge})`;
  if (zoomEnabled) {
    cursorClasses = overlayOpen ? cls("cursor-zoom-out") : cls("cursor-zoom-in");
    if (backgroundActive) {
      bgClasses = cls("bg-1000", "xxxxl:bg-2000");
      bgImage = "//www.webstaurantstore.com/uploads/design/2022/2/pdp-gallery-placeholder-white-1000.png";
      if (overlayOpen) {
        bgClasses = cls("bg-2000", "xxxxl:bg-110-percent");
        bgImage = `url(${imageHost}${medium.extraExtraLarge})`;
      }
    } else {
      bgClasses = cls("bg-0");
    }
  } else {
    bgClasses = cls("bg-0");
  }
  const imageClassStyle = `${cls("leading-none", "max-h-screen", "w-full")}
    ${!isMobileScreenSize && zoomEnabled && backgroundActive ? cls("opacity-0") : undefined}`;
  const imageContent = () => {
    return <>
        <figure id="FigureImage" data-src={overlayOpen ? `url(${imageHost}${medium.extraExtraLarge})` : `url(${imageHost}${medium.extraLarge})`} className={`${cls("bg-no-repeat", "h-full", "m-0", "relative", "w-full", isIE ? "" : "flex-1")}${overlayOpen ? cls("flex", "items-center") : ""} ${cursorClasses} ${bgClasses}`} {...!isMobileScreenSize && zoomEnabled ? {
        onMouseMove: handleMouseMove,
        onMouseLeave: () => setBackgroundActive(false),
        onMouseOver: () => {
          setBackgroundActive(true);
        }
      } : {}} style={{
        backgroundImage: bgImage,
        backgroundPosition,
        maxWidth: "3000px"
      }} ref={imageContainerRef}>
          <div className={cls(overlayOpen ? "inline-block" : "block", "mx-auto", "relative", "lg:h-full", "p-[2px]")} style={{
          maxWidth: overlayOpen ? "100%" : "600px",
          maxHeight: overlayOpen ? "100%" : "600px"
        }}>
            <div className={cls("h-0", "pb-full", "relative")}>
              {overlayOpen ? <img src={`//cdnimg.webstaurantstore.com${medium.extraLarge}`} alt={alt} className={imageClassStyle} /> : <img id="GalleryImage" src={index === 0 || index === 1 && isMobileScreenSize ? src : placeholderImageUrl} data-src={src} alt={alt} className={(cls("absolute", "inset-0"), imageClassStyle)} />}
            </div>
            {imageNote && medium.imageNotesCoordinates && (() => {
            const {
              top,
              left,
              width,
              height
            } = medium.imageNotesCoordinates;
            return <div data-testid="image-note-area" className={cls("absolute")} style={{
              top: `${top}%`,
              left: `${left}%`
            }}>
                    <div className={cls("group", "border-black", "border-solid", "border", "relative")} style={{
                width: `${width * 5}px`,
                height: `${height * 5}px`,
                background: "rgba(255, 255, 255, 0.25)"
              }}>
                      <div className={cls("bg-white", "rounded", "text-sm", "mx-auto", "mt-1", "opacity-0", "group-hover:opacity-100", "p-2", "pointer-events-none", "absolute", "left-1/2", "top-full", "shadow-sm", "text-center", "w-48")} style={{
                  transform: "translateX(-50%)"
                }}>
                        {imageNote}
                      </div>
                    </div>
                  </div>;
          })()}
          </div>
        </figure>
        {imageNote && <div className={cls("h-8", "text-base", "pt-3", "relative", "text-center", "w-full", "z-10", "xl:text-lg")}>
            {imageNote}
          </div>}
      </>;
  };
  return zoomEnabled ? <ModalTrigger data-testid="image-button" {...zoomEnabled ? {
    onClick: openZoom ? e => openZoom(e, medium) : undefined
  } : {}} className={cls("bg-white", "border-1", "focus:outline", "focus:outline-2", "focus:outline-offset-[-2px]", "cursor-pointer", "flex", "flex-col", "items-start", "justify-center", "h-full", "p-0", "relative", "w-full", "outline-offset-[-1px]")} data-sentry-element="ModalTrigger" data-sentry-component="PrimaryImage" data-sentry-source-file="PrimaryImage.tsx">
      {imageContent()}
    </ModalTrigger> : <div className={cls("bg-white", "border-1", "cursor-default", "flex", "flex-col", "items-start", "justify-center", "h-full", "p-0", "relative", "w-full", "outline-offset-[-1px]")} data-sentry-component="PrimaryImage" data-sentry-source-file="PrimaryImage.tsx">
      {imageContent()}
    </div>;
};
export default PrimaryImage;